import React, { useEffect } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import { Stepper } from "@features/Stepper";
import { useTranslation } from "next-i18next";
import { SuccessModal } from "@shared/ui/SuccessModal";
import {
  $applicationStep,
  $successModalVisibility,
  resetApplicationForm,
  setCityApplicationStep2,
  setClassificationApplicationStep4,
  setCreditTypeApplicationStep3,
  setSuccessModalVisibility,
} from "@entities/Product/Application/model";
import { IDictionaryApplicationResponse, IDir } from "@shared/types";
import { extractSelectOptions } from "@shared/lib";
import { routePaths } from "@shared/routes";
import { getStepperData } from "@entities/Product/Application/utils";
import { useUnit } from "effector-react";
import { ApplicationStep1Code } from "@entities/Product/Application/ui/Step1Code";
import { ApplicationStep1 } from "./ui/Step1";
import { ApplicationStep2 } from "./ui/Step2";
import { ApplicationStep3 } from "./ui/Step3";
import { ApplicationStep4 } from "./ui/Step4";
import { ApplicationStep5 } from "./ui/Step5";
import { ApplicationFinalStep } from "./ui/FinalStep";

interface IProps {
  dictionaries: IDictionaryApplicationResponse;
  states: IDir[];
  brands: IDir[];
}

export default function ApplicationForm(props: IProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [
    activeStep,
    setIsSuccessModalVisible,
    isSuccessModalVisible,
    setCityStep2,
    setCreditTypeStep3,
    setClassificationStep4,
    resetForm,
  ] = useUnit([
    $applicationStep,
    setSuccessModalVisibility,
    $successModalVisibility,
    setCityApplicationStep2,
    setCreditTypeApplicationStep3,
    setClassificationApplicationStep4,
    resetApplicationForm,
  ]);

  const citiesOptions = extractSelectOptions(props.dictionaries.cities);

  const creditPeriodOptions = extractSelectOptions(
    props.dictionaries.credit_periods
  );

  const creditTargetOptions = extractSelectOptions(
    props.dictionaries.credit_targets
  );

  const depositTypeOptions = extractSelectOptions(
    props.dictionaries.pledge_types
  );

  const creditTypeOptions = extractSelectOptions(props.dictionaries.products);

  const classificationOptions = extractSelectOptions(
    props.dictionaries.classifications
  );

  const stateOptions = props.states.map((item) => ({
    value: String(item.code),
    label: item.name,
  }));

  const brandsOptions = props.brands.map((item) => ({
    value: String(item.code),
    label: item.name,
  }));

  const closeSuccessModal = async () => {
    setIsSuccessModalVisible(false);
    await router.push(routePaths.main.index);
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return <ApplicationStep1 />;
      case 1:
        return <ApplicationStep1Code />;
      case 2:
        return <ApplicationStep2 cities={citiesOptions} />;
      case 3:
        return (
          <ApplicationStep3
            creditTypeOptions={creditTypeOptions}
            periodOptions={creditPeriodOptions}
            creditAimOptions={creditTargetOptions}
            carBrandOptions={brandsOptions}
          />
        );
      case 4:
        return (
          <ApplicationStep4 classificationOptions={classificationOptions} />
        );
      case 5:
        return (
          <ApplicationStep5
            pledgeTypes={depositTypeOptions}
            states={stateOptions}
            brandOptions={brandsOptions}
          />
        );
      case 6:
        return (
          <ApplicationFinalStep
            cities={citiesOptions}
            creditTypeOptions={creditTypeOptions}
            periodOptions={creditPeriodOptions}
            creditAimOptions={creditTargetOptions}
            classificationOptions={classificationOptions}
            pledgeTypes={depositTypeOptions}
            stateOptions={stateOptions}
            carBrandOptions={brandsOptions}
          />
        );
      default:
        return "Unknown step";
    }
  }

  const stepperData = getStepperData(
    activeStep,
    t,
    router.query?.productId as string
  );

  useEffect(
    () => () => {
      resetForm();
    },
    []
  );

  useEffect(() => {
    setCityStep2(citiesOptions[0].value);
    setCreditTypeStep3(
      creditTypeOptions.find(
        (product) => product.value === router.query?.productId
      )?.value ?? ""
    );
    setClassificationStep4(classificationOptions[0].value);
  }, [router.query?.productId]);

  return (
    <>
      {activeStep < 6 && (
        <StyledStepper
          step={stepperData.step}
          title={stepperData.title}
          percent={stepperData.percent}
        />
      )}
      <>{getStepContent(activeStep)}</>
      <SuccessModal
        show={isSuccessModalVisible}
        onClose={closeSuccessModal}
        title={t(`Ваша заявка успешно отправлена!`)}
        description={t(
          `Мы свяжемся с вами в ближайшее время после рассмотрения вашей заявки`
        )}
        buttonLabel={t(`Понятно`)}
      />
    </>
  );
}

const StyledStepper = styled(Stepper)`
  margin-bottom: 6.4rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    margin-bottom: 2.4rem;
  }
`;
